/**
 * Document View Types
 *
 * Adapter types that bridge between the backend UIDocument shape and
 * the richer mock UI Document model. Mock-only fields are optional
 * with sensible defaults applied by toDocumentView().
 */

import type { UIDocument, UserId } from "./compatibility";
import type { DocumentId, Id } from "./index";

/* ------------------------------------------------------------------ */
/* Static types                                                        */
/* ------------------------------------------------------------------ */

export type DocumentStatus = "final" | "draft" | "under-review" | "expired";

export type DocumentCategory =
  | "Report"
  | "Certificate"
  | "Diagram"
  | "Analysis"
  | "Policy"
  | "Spreadsheet"
  | "Contract"
  | "Checklist"
  | "Uncategorized";

export type DocumentViewFileType =
  | "pdf"
  | "docx"
  | "xlsx"
  | "pptx"
  | "txt"
  | "image";

export interface DocumentCategoryView {
  _id: Id<"documentCategories">;
  name: string;
  icon: string;
  isSystem: boolean;
  order: number;
}

export type ProcessingStepStatus =
  | "pending"
  | "generating"
  | "completed"
  | "failed"
  | "retrying";

export interface DocumentAuthor {
  name: string;
  avatarUrl?: string;
  avatarColor?: string;
}

export interface DocumentChecklistItem {
  id: string;
  label: string;
  done: boolean;
  assignee: string;
}

export interface DocumentAiSummary {
  text: string;
  status: "generating" | "ready";
}

/* ------------------------------------------------------------------ */
/* DocumentView — the shape every UI component consumes                */
/* ------------------------------------------------------------------ */

export interface DocumentViewTag {
  id: string;
  name: string;
  color: string;
}

export interface DocumentView {
  id: DocumentId;
  title: string;
  type: DocumentViewFileType;
  size: string;
  updatedAt: string;
  status: DocumentStatus;

  /** Real backend field — mapped from UIDocument.summary */
  description?: string;

  /** Real backend field */
  tags?: DocumentViewTag[];

  /** Real backend field — raw user ID for author lookup */
  uploadedBy?: UserId;

  /* -- Category fields (real backend fields) -- */
  /** Real backend field — FK to documentCategories table */
  categoryId?: Id<"documentCategories">;
  /** Display field — populated by join with documentCategories table */
  categoryName?: string;
  /** Display field — populated by join with documentCategories table */
  categoryIcon?: string;

  /* -- Mock-only fields (optional, with fallbacks) -- */
  author?: DocumentAuthor;
  /** @deprecated Use categoryId/categoryName/categoryIcon instead */
  category?: DocumentCategory;
  collaborators?: DocumentAuthor[];
  commentCount?: number;
  version?: string;
  confidential?: boolean;
  thumbnail?: string;
  checklist?: DocumentChecklistItem[];
  aiSummary?: DocumentAiSummary;

  /* -- Processing status fields -- */
  isProcessing?: boolean;
  processingSteps?: {
    summary: ProcessingStepStatus;
    checklistMatching: ProcessingStepStatus;
  };

  /* -- Expiration date fields -- */
  expirationDate?: string;
  expirationDateTimestamp?: number;

  /* -- Version & diff fields -- */
  currentVersion?: number;
  diffSummary?:
    | string
    | {
        changeStatus: "identical" | "different";
        magnitude?: "minor" | "moderate" | "major";
        businessSummary: string;
        textDiff?: {
          removed: string;
          added: string;
        };
        structuralChanges: string;
      };
  diffSummaryStatus?:
    | "pending"
    | "generating"
    | "completed"
    | "failed"
    | "retrying";
}

/* ------------------------------------------------------------------ */
/* Mapping helpers                                                     */
/* ------------------------------------------------------------------ */

const FILE_TYPE_MAP: Record<string, DocumentViewFileType> = {
  pdf: "pdf",
  docx: "docx",
  doc: "docx",
  xlsx: "xlsx",
  xls: "xlsx",
  pptx: "pptx",
  ppt: "pptx",
  txt: "txt",
  csv: "txt",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  svg: "image",
  webp: "image",
};

function mapFileType(raw?: string): DocumentViewFileType {
  if (!raw) return "pdf";
  return FILE_TYPE_MAP[raw.toLowerCase()] ?? "pdf";
}

function mapStatus(summaryStatus?: string): DocumentStatus {
  switch (summaryStatus) {
    case "completed":
      return "final";
    case "generating":
    case "retrying":
      return "under-review";
    case "failed":
      return "expired";
    default:
      return "draft";
  }
}

function mapAiSummary(
  summary?: string,
  summaryStatus?: string,
): DocumentAiSummary | undefined {
  if (!summary && !summaryStatus) return undefined;
  return {
    text: summary ?? "",
    status: summaryStatus === "generating" ? "generating" : "ready",
  };
}

function isActiveProcessing(status?: string): boolean {
  return (
    status === "pending" || status === "generating" || status === "retrying"
  );
}

function mapProcessingStep(status?: string): ProcessingStepStatus {
  if (!status) return "pending";
  if (
    status === "pending" ||
    status === "generating" ||
    status === "completed" ||
    status === "failed" ||
    status === "retrying"
  ) {
    return status;
  }
  return "pending";
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Transform a backend UIDocument into the view-model shape
 * that all document feature components expect.
 *
 * Note: categoryName and categoryIcon are NOT populated by this function.
 * They must be populated by the caller via join with documentCategories table.
 */
export function toDocumentView(doc: UIDocument): DocumentView {
  const summaryStep = mapProcessingStep(doc.summaryStatus);
  const checklistStep = mapProcessingStep(doc.suggestedChecklistItemsStatus);
  const isProcessing =
    isActiveProcessing(doc.summaryStatus) ||
    isActiveProcessing(doc.suggestedChecklistItemsStatus) ||
    isActiveProcessing(doc.tagGenerationStatus);

  return {
    id: doc.id,
    title: doc.fileName ?? "Untitled",
    type: mapFileType(doc.fileType),
    size: "\u2014", // em-dash fallback — no file size from backend
    updatedAt: formatDate(doc._creationTime),
    status: mapStatus(doc.summaryStatus),
    description: doc.summary,
    tags: doc.tags?.map((t) => ({ id: t.id, name: t.name, color: t.color })),

    // Processing status
    isProcessing,
    processingSteps: {
      summary: summaryStep,
      checklistMatching: checklistStep,
    },

    categoryId: doc.categoryId ?? undefined,
    categoryName: doc.categoryName ?? undefined,
    categoryIcon: doc.categoryIcon ?? undefined,

    expirationDate: doc.expirationDate
      ? formatDate(doc.expirationDate)
      : undefined,
    expirationDateTimestamp: doc.expirationDate ?? undefined,

    author: { name: doc.authorName ?? "Unknown" },
    version: `v${doc.currentVersion ?? 1}`,
    aiSummary: mapAiSummary(doc.summary, doc.summaryStatus),
    checklist: doc.linkedChecklistItems?.map((item) => ({
      id: item.id,
      label: item.label,
      done: item.done,
      assignee: "",
    })),
    uploadedBy: doc.uploadedBy,
    currentVersion: doc.currentVersion ?? 1,
    diffSummary: doc.diffSummary ?? undefined,
    diffSummaryStatus: doc.diffSummaryStatus ?? undefined,
  };
}
